import { useEffect, useMemo, useRef, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import {
  DownloadOutlined, DownOutlined, FilePdfOutlined, SearchOutlined, SettingOutlined, UpOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import {
  deleteSolicitud, getCfdiCatalogo, getCfdiCatalogoExportStatus, getCfdiCatalogoPdfUrl, getSolicitudEstado,
  getSolicitudesPorRfc, iniciarCfdiCatalogoExport, postSolicitar, postVerificar,
} from '../api';
import type { CfdiCatalogoConteoPorMes, CfdiCatalogoFiltros, CfdiCatalogoItem, CfdiDireccion, SolicitudDetalle } from '../api';
import { ColumnasExcelModal, EmptyState, Loader, SolicitudesEstadoPorMes } from '../components';
import { formatMonto } from '../utils/format';
import { usePersistedState } from '../utils/usePersistedState';
import { useEmpresa, EMPRESA_TODAS } from '../empresa';
import { useFiltroFecha } from '../filtroFecha';

const { Title, Paragraph, Text } = Typography;

const TIPO_DOC_LABEL: Record<string, string> = {
  I: 'Ingreso',
  E: 'Egreso',
  T: 'Traslado',
  N: 'Nómina',
  P: 'Pago',
};

// Sub-tabs por tipo de documento. 'TODOS' agrupa I/E (facturas de
// ingreso/egreso se muestran juntas, como en el resto de la app) y el resto
// son sub-tabs 1:1 con tipo_doc.
const SUB_TABS: { key: string; label: string; tipoDoc?: string }[] = [
  { key: 'TODOS', label: 'Todos' },
  { key: 'FACTURAS', label: 'Facturas (I/E)' },
  { key: 'P', label: 'Pagos', tipoDoc: 'P' },
  { key: 'N', label: 'Nómina', tipoDoc: 'N' },
  { key: 'T', label: 'Traslados', tipoDoc: 'T' },
];

const PAGE_SIZE = 25;
const MONTO_DEBOUNCE_MS = 400;

// Persistencia del polling de "Solicitar" en curso — sin esto (a diferencia
// de CargaArchivos.tsx, que guarda su cola completa en localStorage), un
// reload durante los ~3 min de espera pierde el estado y el botón vuelve a
// "Solicitar al SAT" como si nada estuviera corriendo, aunque el SAT siga
// procesando en el backend.
const LS_DESCARGA_POLL_KEY = 'asi_cfdi_catalogo_descarga_poll_v1';

interface DescargaPollState {
  rfc: string;
  started: string;
}

function loadDescargaPollState(): DescargaPollState | null {
  try {
    const raw = localStorage.getItem(LS_DESCARGA_POLL_KEY);
    return raw ? JSON.parse(raw) as DescargaPollState : null;
  } catch { return null; }
}
function saveDescargaPollState(state: DescargaPollState | null) {
  if (state) localStorage.setItem(LS_DESCARGA_POLL_KEY, JSON.stringify(state));
  else localStorage.removeItem(LS_DESCARGA_POLL_KEY);
}

export function CfdiCatalogo() {
  const { message, notification } = App.useApp();
  const { rfc, loading: loadingEmpresas } = useEmpresa();
  const { rango, setRango } = useFiltroFecha();

  const [items, setItems] = useState<CfdiCatalogoItem[]>([]);
  const [total, setTotal] = useState(0);
  const [conteoPorMes, setConteoPorMes] = useState<CfdiCatalogoConteoPorMes[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [columnasModalOpen, setColumnasModalOpen] = useState(false);
  const [exportando, setExportando] = useState(false);
  // UUID del CFDI cuyo PDF se está generando ahora — null si ninguno.
  // Ver GET /cfdi-catalogo/{rfc}/pdf/{uuid} (cfdi_catalogo_pdf.py).
  const [generandoPdfUuid, setGenerandoPdfUuid] = useState<string | null>(null);

  const [subTab, setSubTab] = usePersistedState<string>('cfdiCatalogo.subTab', 'TODOS');
  const [direccion, setDireccion] = usePersistedState<string>('cfdiCatalogo.direccion', 'TODOS');
  const [folio, setFolio] = usePersistedState('cfdiCatalogo.folio', '');
  const [uuid, setUuid] = usePersistedState('cfdiCatalogo.uuid', '');
  // Filtro por RFC de la contraparte (emisor o receptor) y por monto, mismo
  // patrón de debounce que ReporteDetalle.tsx para no disparar un fetch por
  // cada tecla.
  const [filtroRfc, setFiltroRfc] = usePersistedState('cfdiCatalogo.rfc', '');
  const [montoMin, setMontoMin]       = usePersistedState<number | null>('cfdiCatalogo.montoMin', null);
  const [montoMax, setMontoMax]       = usePersistedState<number | null>('cfdiCatalogo.montoMax', null);
  const [montoMinDeb, setMontoMinDeb] = useState<number | null>(null);
  const [montoMaxDeb, setMontoMaxDeb] = useState<number | null>(null);
  // Evita repetir el aviso en cada tecla mientras el rango siga inválido.
  const montoInvalidoAvisadoRef = useRef(false);

  // Descargar CFDIs del SAT directo desde este catálogo (sin pasar por la
  // pestaña "Conciliar" de Carga de Archivos, que además pide subir un
  // extracto bancario). Mismo mecanismo de 2 pasos (Solicitar + Verificar)
  // que CargaArchivos.tsx, simplificado a un solo trabajo a la vez en vez
  // de una cola: acá no hace falta encolar varios RFCs, el RFC ya está fijo
  // por el selector de empresa de arriba.
  const [descargaRango, setDescargaRango] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [descargaSolicitudIds, setDescargaSolicitudIds] = useState<string[] | null>(null);
  const [descargaPollIds, setDescargaPollIds] = useState<string[] | null>(null);
  // true mientras SATSolicitar corre async tras disparar Solicitar (rango largo).
  const [isDescargaSolicitudEnProceso, setIsDescargaSolicitudEnProceso] = useState(false);
  // Timestamp del backend (disparado_en) — mismo valor que usa el marcador
  // de troceo completo, no generado en el navegador.
  const [descargaSolicitudEnProcesoStarted, setDescargaSolicitudEnProcesoStarted] = useState<string | null>(null);
  // RFC capturado al disparar — si el usuario cambia de empresa a mitad del
  // polling, se detecta el mismatch y se cancela solo (ver poll() más abajo).
  const [descargaSolicitudEnProcesoContexto, setDescargaSolicitudEnProcesoContexto] = useState<{
    rfc: string;
  } | null>(null);
  const [solicitando, setSolicitando] = useState(false);
  const [verificando, setVerificando] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [isHistorialColapsado, setIsHistorialColapsado] = usePersistedState('cfdiCatalogo.historialColapsado', false);

  // Restaura el polling de Solicitar si quedó en curso antes de un reload.
  useEffect(() => {
    const stored = loadDescargaPollState();
    if (stored) {
      setIsDescargaSolicitudEnProceso(true);
      setDescargaSolicitudEnProcesoStarted(stored.started);
      setDescargaSolicitudEnProcesoContexto({ rfc: stored.rfc });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Si el usuario cambia de empresa mientras hay un polling en curso, se
  // limpia el estado de inmediato en vez de esperar al próximo tick del
  // poll (~3s) — sin esto, el botón/DatePicker de la empresa NUEVA se ven
  // deshabilitados/en loading por una solicitud que no es la suya.
  const previousRfcRef = useRef<string | null>(null);
  useEffect(() => {
    if (loadingEmpresas) return;
    if (previousRfcRef.current === null) {
      previousRfcRef.current = rfc; // primera resolución de rfc, no es un cambio de empresa
      return;
    }
    if (previousRfcRef.current === rfc) return;
    previousRfcRef.current = rfc;
    setIsDescargaSolicitudEnProceso(false);
    setDescargaSolicitudEnProcesoStarted(null);
    setDescargaSolicitudEnProcesoContexto(null);
    setDescargaRango([null, null]);
    saveDescargaPollState(null);
  }, [rfc, loadingEmpresas]);

  // Foto de qué meses ya se pidieron al SAT y cómo quedó cada uno
  // (completada/error/en curso), agrupado por mes calendario — ver
  // SolicitudesEstadoPorMes. Se refresca con el mismo refreshTick que
  // dispara el listado del catálogo (cuando Verificar termina un lote).
  const [solicitudesHistorial, setSolicitudesHistorial] = useState<SolicitudDetalle[]>([]);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);

  // Filtro por monto con debounce (patrón de ReporteDetalle). Si el rango es
  // inválido no se aplica y se avisa (una vez); queda el último rango válido.
  useEffect(() => {
    const invalido = montoMin != null && montoMax != null && montoMin > montoMax;
    if (invalido) {
      if (!montoInvalidoAvisadoRef.current) {
        message.warning('El monto mínimo no puede ser mayor que el monto máximo.');
        montoInvalidoAvisadoRef.current = true;
      }
      return;
    }
    montoInvalidoAvisadoRef.current = false;
    const t = setTimeout(() => {
      setMontoMinDeb(montoMin);
      setMontoMaxDeb(montoMax);
    }, MONTO_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [montoMin, montoMax, message]);

  // 'FACTURAS' no es un tipo_doc real — se manda como CSV 'I,E' (el backend
  // acepta lista vía tipo_doc=I,E); el resto de sub-tabs mapea 1:1 con
  // tipo_doc. Nunca se filtra client-side: eso rompería total/paginación,
  // que ya vienen correctos desde el backend para el filtro combinado.
  const tipoDocBackend = useMemo(() => {
    if (subTab === 'FACTURAS') return 'I,E';
    return SUB_TABS.find(t => t.key === subTab)?.tipoDoc;
  }, [subTab]);

  const desde = rango?.[0] ? rango[0].format('YYYY-MM-DD') : undefined;
  const hasta = rango?.[1] ? rango[1].format('YYYY-MM-DD') : undefined;

  useEffect(() => { setPage(1); }, [rfc, subTab, direccion, folio, uuid, desde, hasta, filtroRfc, montoMinDeb, montoMaxDeb]);

  // Filtros sin page/page_size — reusados por el fetch del listado
  // (+paginación) y por el export a Excel (que trae TODO el conjunto
  // filtrado, no una página). Antes se duplicaba este objeto a mano en
  // ambos lugares; centralizarlo evita que se desincronicen si se agrega
  // un filtro nuevo. Mismo patrón que filtrosBase en ReporteDetalle.tsx.
  const filtrosBase: CfdiCatalogoFiltros = useMemo(() => ({
    tipo_doc: tipoDocBackend,
    direccion: direccion !== 'TODOS' ? (direccion as CfdiDireccion) : undefined,
    folio: folio.trim() || undefined,
    uuid: uuid.trim() || undefined,
    desde,
    hasta,
    rfc: filtroRfc.trim() || undefined,
    monto_min: montoMinDeb ?? undefined,
    monto_max: montoMaxDeb ?? undefined,
  }), [tipoDocBackend, direccion, folio, uuid, desde, hasta, filtroRfc, montoMinDeb, montoMaxDeb]);

  useEffect(() => {
    if (rfc === EMPRESA_TODAS) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getCfdiCatalogo(rfc, { ...filtrosBase, page, page_size: PAGE_SIZE })
      .then(res => {
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
        setConteoPorMes(res.conteo_por_mes);
      })
      .catch(() => {
        if (!cancelled) {
          setError('No se Pudo Cargar el Catálogo de CFDIs.');
          // Sin conteo real no se descarta ningún mes (ver agruparPorMes);
          // además evita arrastrar el conteo de otro RFC si el fetch falla.
          setConteoPorMes([]);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [rfc, subTab, filtrosBase, page, refreshTick]);

  // Historial de solicitudes al SAT por mes — independiente de los filtros
  // de la tabla de abajo (folio, monto, etc.), solo depende del RFC y de
  // refreshTick (se recarga cuando Verificar termina un lote).
  useEffect(() => {
    if (rfc === EMPRESA_TODAS) return;
    let cancelled = false;
    setCargandoHistorial(true);
    getSolicitudesPorRfc(rfc)
      .then(res => { if (!cancelled) setSolicitudesHistorial(res.solicitudes); })
      .catch(() => { if (!cancelled) setSolicitudesHistorial([]); })
      .finally(() => { if (!cancelled) setCargandoHistorial(false); });
    return () => { cancelled = true; };
  }, [rfc, refreshTick]);

  // Cancelar (dejar de rastrear localmente) las sub-solicitudes de una fila
  // de SolicitudesEstadoPorMes — no cancela nada en el SAT, ver el texto del
  // Popconfirm en ese componente. allSettled porque un id ya puede haber
  // sido borrado por otra pestaña/usuario (404) sin que eso sea un fallo real.
  async function handleCancelarMes(ids: string[]) {
    const resultados = await Promise.allSettled(ids.map(id => deleteSolicitud(id)));
    const exitosas = resultados.filter(r => r.status === 'fulfilled').length;
    const fallidas = resultados.length - exitosas;
    if (fallidas === 0) {
      message.success(`${exitosas} solicitud(es) cancelada(s).`);
    } else if (exitosas === 0) {
      message.error('No se pudo cancelar ninguna solicitud.');
    } else {
      message.warning(`${exitosas} cancelada(s), ${fallidas} no se pudieron cancelar.`);
    }
    setRefreshTick(t => t + 1);
  }

  // El Excel del catálogo (con columnas CFDI configurables, "Editar
  // Columnas") es async por la misma razón que el de Reportes: re-parsear
  // XML de S3 puede superar los 29s que tolera una request síncrona de API
  // Gateway REST. iniciarCfdiCatalogoExport dispara el job; acá hacemos
  // polling hasta que termine — mismo intervalo/tope que
  // esperarDescargaFiltrada en ReporteDetalle.tsx.
  async function esperarExportCatalogo(jobId: string): Promise<string | null> {
    const INTERVALO_MS = 3000;
    const MAX_INTENTOS = 40; // ~2 minutos
    for (let intento = 0; intento < MAX_INTENTOS; intento++) {
      await new Promise(resolve => setTimeout(resolve, INTERVALO_MS));
      const status = await getCfdiCatalogoExportStatus(rfc, jobId);
      if (status.estado === 'COMPLETADO') {
        if (!status.url) {
          // "Completado" sin URL es falla real: antes no avisaba nada.
          message.error('El Excel se generó pero no se pudo obtener su link. Intenta de nuevo.');
          return null;
        }
        return status.url;
      }
      if (status.estado === 'ERROR') {
        message.error(status.mensaje || 'No se pudo generar el Excel.');
        return null;
      }
    }
    message.error('La generación del Excel está tardando más de lo esperado. Intenta de nuevo en un momento.');
    return null;
  }

  async function descargarExcel() {
    // La selección de columnas ("Editar Columnas") no viaja acá — el
    // backend la resuelve por el usuario autenticado (GET/PUT
    // /preferencias/columnas-excel), no por query param.
    setExportando(true);
    try {
      const { job_id } = await iniciarCfdiCatalogoExport(rfc, filtrosBase);
      const url = await esperarExportCatalogo(job_id);
      // No hacer window.open() acá directo: para cuando el job termina (hasta
      // ~2 min de polling) ya pasó demasiado tiempo desde el click original y
      // el navegador lo trata como popup no solicitado — lo bloquea en
      // silencio, sin error visible. Mostramos un botón que el usuario
      // clickea, así el window.open queda dentro de un gesto real.
      if (url) {
        notification.success({
          message: 'Excel listo',
          description: 'El catálogo filtrado ya se generó.',
          btn: <Button type="primary" size="small" onClick={() => window.open(url, '_blank')}>Descargar</Button>,
          duration: 0,
        });
      } else {
        // Cubre null devuelto por otra vía sin mensaje previo.
        message.error('No se Pudo Generar el Excel.');
      }
    } catch {
      message.error('No se Pudo Generar el Excel.');
    } finally {
      setExportando(false);
    }
  }

  // Ver PDF de un CFDI puntual (columna UUID) — el backend genera el PDF la
  // primera vez (o reusa el ya generado) y devuelve un presigned URL. A
  // diferencia del Excel, esto suele resolver en menos de 1-2s (un solo XML,
  // no un catálogo completo), así que el window.open directo todavía cae
  // dentro del gesto de click del usuario y no lo bloquea el navegador.
  async function verPdfCfdi(uuidCfdi: string) {
    setGenerandoPdfUuid(uuidCfdi);
    try {
      const { url } = await getCfdiCatalogoPdfUrl(rfc, uuidCfdi);
      window.open(url, '_blank');
    } catch {
      message.error('No se Pudo Generar el PDF de este CFDI.');
    } finally {
      setGenerandoPdfUuid(null);
    }
  }

  // ── Descargar CFDIs del SAT (Solicitar) ──────────────────────────────────
  async function solicitarDescarga() {
    const [ini, fin] = descargaRango;
    if (!ini || !fin) { message.warning('Selecciona un rango de fechas.'); return; }
    const dias = fin.diff(ini, 'day');
    const maxDias = 5 * 365 + 2; // ~5 años, mismo tope que CargaArchivos.tsx
    if (dias < 0) { message.warning('La fecha final debe ser igual o posterior a la fecha inicial.'); return; }
    if (dayjs().diff(ini, 'day') > maxDias) { message.warning('El SAT solo permite descargas de hasta 5 años de antigüedad.'); return; }
    if (fin.isAfter(dayjs(), 'day')) { message.warning('La fecha final no puede ser posterior a hoy.'); return; }

    setSolicitando(true);
    try {
      const fechaInicioStr = ini.format('YYYY-MM-DD');
      const fechaFinStr = fin.format('YYYY-MM-DD');
      const result = await postSolicitar({
        rfc,
        fecha_inicio: fechaInicioStr,
        fecha_fin: fechaFinStr,
      });

      if (result.estado === 'en_proceso') {
        // Rango largo — dispara SATSolicitar async, se detecta con polling.
        const started = result.disparado_en ?? new Date().toISOString();
        setIsDescargaSolicitudEnProceso(true);
        setDescargaSolicitudEnProcesoStarted(started);
        setDescargaSolicitudEnProcesoContexto({ rfc });
        saveDescargaPollState({ rfc, started });
        message.info('SAT: solicitud en proceso — actualizando en unos segundos...');
        setRefreshTick(t => t + 1);
        return;
      }

      const ids = result.solicitudes.map(s => s.id_solicitud);
      setDescargaSolicitudIds(ids.length === 0 ? null : ids);
      if (result.reutilizadas) {
        message.warning(
          'Ya hay solicitudes sin resolver para este RFC — no se pidió nada nuevo al SAT. '
          + 'Presiona Verificar, o cancélalas en "Estado de descargas al SAT por mes" para poder solicitar de nuevo.',
        );
      } else {
        message.success(
          ids.length === 0
            ? 'El SAT no aceptó ninguna sub-solicitud — reintenta Solicitar.'
            : `SAT: ${ids.length} solicitud(es) enviada(s). Espera unos minutos y presiona Verificar.`,
        );
      }
      if (ids.length > 0) setRefreshTick(t => t + 1);
    } catch {
      message.error('No se Pudo Solicitar la Descarga al SAT.');
    } finally {
      setSolicitando(false);
    }
  }

  // Polling de la fase "en_proceso" de Solicitar (mismo timeout de 3 min que Verificar).
  useEffect(() => {
    if (!isDescargaSolicitudEnProceso || !descargaSolicitudEnProcesoContexto) return;
    const contexto = descargaSolicitudEnProcesoContexto;
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    async function poll() {
      if (rfc !== contexto.rfc) {
        // Empresa cambió a mitad del polling — se cancela.
        setIsDescargaSolicitudEnProceso(false);
        setDescargaSolicitudEnProcesoStarted(null);
        setDescargaSolicitudEnProcesoContexto(null);
        saveDescargaPollState(null);
        return;
      }

      // descargaSolicitudEnProcesoStarted puede venir corrupto del backend
      // (formato no parseable) — sin este guard, elapsedMin da NaN y
      // `NaN > 3` nunca es true, dejando el polling corriendo para siempre.
      const startedMs = descargaSolicitudEnProcesoStarted ? new Date(descargaSolicitudEnProcesoStarted).getTime() : null;
      const isStartedValido = startedMs == null || Number.isFinite(startedMs);
      const elapsedMin = startedMs == null
        ? 0
        : Number.isFinite(startedMs) ? (Date.now() - startedMs) / 60_000 : Infinity;

      let rescateIds: string[] | null = null;

      try {
        const result = await getSolicitudesPorRfc(contexto.rfc);
        if (cancelled) return;

        if (result.solicitudes.some(s => !s.fecha_solicitud)) {
          console.warn(`getSolicitudesPorRfc(${contexto.rfc}): ${result.solicitudes.filter(s => !s.fecha_solicitud).length} sub-solicitud(es) sin fecha_solicitud — quedan fuera de la correlación por timestamp.`);
        }

        // Margen de 5s por drift de reloj navegador/servidor.
        const disparadoEn = descargaSolicitudEnProcesoStarted
          ? new Date(descargaSolicitudEnProcesoStarted).getTime() - 5_000 : null;
        const dentroDeEstePedido = result.solicitudes.filter(s =>
          !!s.fecha_solicitud && disparadoEn != null && new Date(s.fecha_solicitud).getTime() >= disparadoEn);
        rescateIds = dentroDeEstePedido.map(s => s.id);

        // El backend guarda un marcador (troceos_completos) al terminar de
        // crear todas las sub-ventanas — se usa esa señal explícita en vez
        // de adivinar por conteo estable entre ticks.
        const isTroceoCompleto = (result.troceos_completos ?? []).some(m =>
          disparadoEn != null && new Date(m.fecha_solicitud).getTime() >= disparadoEn);
        if (isTroceoCompleto) {
          const nuevosIds = dentroDeEstePedido.map(s => s.id);
          // null (no []) cuando no se aceptó nada — un array vacío deja el
          // DatePicker/botón Solicitar bloqueados para siempre (!= null).
          setDescargaSolicitudIds(nuevosIds.length === 0 ? null : nuevosIds);
          setIsDescargaSolicitudEnProceso(false);
          setDescargaSolicitudEnProcesoStarted(null);
          setDescargaSolicitudEnProcesoContexto(null);
          saveDescargaPollState(null);
          const activas = dentroDeEstePedido.filter(s => s.estado === 'pendiente' || s.estado === 'en_proceso');
          message.success(
            nuevosIds.length === 0
              ? 'El SAT no aceptó ninguna sub-solicitud — reintenta Solicitar.'
              : activas.length === nuevosIds.length
                ? `SAT: ${nuevosIds.length} solicitud(es) creada(s). Espera unos minutos y presiona Verificar.`
                : `SAT: ${nuevosIds.length} solicitud(es) creada(s) (${nuevosIds.length - activas.length} ya resuelta(s) por el proceso automático). Presiona Verificar para el resto.`,
          );
          setRefreshTick(t => t + 1);
          return;
        }
      } catch {
        // Error de red puntual — se reintenta en el siguiente tick, salvo timeout (ver abajo).
      }

      if (!isStartedValido) {
        console.error(`Polling de Solicitar: descargaSolicitudEnProcesoStarted inválido para rfc ${contexto.rfc}:`, descargaSolicitudEnProcesoStarted);
      }
      // Timeout de 3 min — corre siempre, incluso si getSolicitudesPorRfc
      // falla en cada tick (sin esto, un error de red persistente deja el
      // polling corriendo para siempre). Si ya se habían visto sub-solicitudes
      // creadas antes de cumplirse el timeout, se rescatan en vez de perderlas
      // (mismo criterio que el poll de CargaArchivos.tsx).
      if (elapsedMin > 3) {
        const hasRescate = !!rescateIds && rescateIds.length > 0;
        setDescargaSolicitudIds(hasRescate ? rescateIds : null);
        setIsDescargaSolicitudEnProceso(false);
        setDescargaSolicitudEnProcesoStarted(null);
        setDescargaSolicitudEnProcesoContexto(null);
        saveDescargaPollState(null);
        message.warning(
          hasRescate
            ? `El SAT está tardando más de lo esperado, pero se recuperaron ${rescateIds!.length} sub-solicitud(es) ya creada(s). Presiona Verificar.`
            : 'El SAT sigue procesando la solicitud. Intenta Solicitar de nuevo en unos minutos.',
        );
        setRefreshTick(t => t + 1);
        return;
      }
      if (!cancelled) timeoutId = setTimeout(poll, 4_000);
    }

    timeoutId = setTimeout(poll, 3_000);
    return () => { cancelled = true; clearTimeout(timeoutId); };
  }, [isDescargaSolicitudEnProceso, descargaSolicitudEnProcesoContexto, rfc]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Verificar (dispara el polling de abajo) ──────────────────────────────
  async function verificarDescarga() {
    if (!descargaSolicitudIds) return;
    setVerificando(true);
    try {
      const result = await postVerificar(descargaSolicitudIds);
      if (result.estado === 'sin_pendientes' || result.ids.length === 0) {
        message.info('No hay solicitudes pendientes de verificar.');
        setDescargaSolicitudIds(null);
        return;
      }
      setDescargaPollIds(result.ids);
    } catch {
      message.error('No se Pudo Verificar la Descarga.');
    } finally {
      setVerificando(false);
    }
  }

  // Polling de la verificación en curso — mismo criterio de estados
  // terminales/timeout que el poll de trabajos en CargaArchivos.tsx, acá
  // simplificado a un solo trabajo (no hay cola de RFCs en esta pantalla).
  useEffect(() => {
    if (!descargaPollIds) return;
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;
    const TERMINALES = new Set(['completada', 'completada_sin_datos', 'error']);
    const inicio = Date.now();

    async function poll() {
      try {
        const estados = await Promise.all(descargaPollIds!.map(getSolicitudEstado));
        if (cancelled) return;
        const normalizados = estados.map(e =>
          (e.estado === 'en_proceso' && e.mensaje_error) ? { ...e, estado: 'error' } : e);

        if (normalizados.every(e => TERMINALES.has(e.estado))) {
          const completadas = normalizados.filter(e => e.estado === 'completada' || e.estado === 'completada_sin_datos').length;
          const errores = normalizados.filter(e => e.estado === 'error').length;
          setDescargaPollIds(null);
          setDescargaSolicitudIds(null);
          if (completadas === 0 && errores > 0) {
            const primero = normalizados.find(e => e.estado === 'error');
            message.error(`El SAT rechazó/falló la verificación: ${primero?.mensaje_error ?? primero?.mensaje ?? 'motivo desconocido'}`);
          } else if (completadas > 0) {
            message.success(`Descarga completa: ${completadas} paquete(s) de CFDIs listos.`);
            setRefreshTick(t => t + 1);
          } else {
            message.info('Verificación completa. No se descargaron CFDIs nuevos.');
          }
          return;
        }

        // El worker de fondo (EventBridge, cada 15 min) procesa un máximo de
        // BATCH_MAX_SOLICITUDES_POR_INVOCACION filas por corrida (ver
        // verificar_descarga.py) — con muchas sub-solicitudes pendientes
        // (rangos largos troceados por mes), puede necesitar varias corridas
        // para terminar todas. 3 min fijos alcanzan para pocas sub-solicitudes
        // pero no para un rango de varios meses/años — se escala el timeout
        // según cuántas hay en esta verificación, sin bloquear al usuario:
        // el worker sigue avanzando en background aunque cierre la pestaña.
        const timeoutMin = Math.max(3, Math.ceil(descargaPollIds!.length / 10) * 15 + 3);
        if ((Date.now() - inicio) / 60_000 > timeoutMin) {
          setDescargaPollIds(null);
          message.warning(
            descargaPollIds!.length > 10
              ? 'El SAT sigue procesando este rango — al ser varias sub-solicitudes, el worker de fondo puede tardar hasta 30-45 min en resolverlas todas. Presiona Verificar más tarde, no hace falta dejar la pestaña abierta.'
              : 'El SAT aún está procesando. Presiona Verificar de nuevo en unos minutos.',
          );
          return;
        }
      } catch {
        // Error de red puntual — se reintenta en el siguiente tick.
      }
      if (!cancelled) timeoutId = setTimeout(poll, 4_000);
    }

    timeoutId = setTimeout(poll, 2_000);
    return () => { cancelled = true; clearTimeout(timeoutId); };
  }, [descargaPollIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns: ColumnsType<CfdiCatalogoItem> = [
    {
      title: 'Tipo',
      key: 'tipo_doc',
      width: 100,
      render: (_, i) => <Tag>{TIPO_DOC_LABEL[i.tipo_doc] || i.tipo_doc}</Tag>,
    },
    {
      title: 'Dirección',
      key: 'direccion',
      width: 100,
      render: (_, i) => (
        <Tag color={i.direccion === 'emitidos' ? 'blue' : 'purple'}>
          {i.direccion === 'emitidos' ? 'Emitido' : 'Recibido'}
        </Tag>
      ),
    },
    { title: 'Fecha', dataIndex: 'fecha_emision', key: 'fecha_emision', width: 110 },
    {
      title: 'Folio',
      key: 'folio',
      width: 110,
      render: (_, i) => [i.serie, i.folio].filter(Boolean).join('-') || '—',
    },
    { title: 'RFC Emisor', dataIndex: 'rfc_emisor', key: 'rfc_emisor', width: 130 },
    { title: 'RFC Receptor', dataIndex: 'rfc_receptor', key: 'rfc_receptor', width: 130 },
    {
      title: 'Total',
      key: 'total',
      width: 130,
      align: 'right',
      render: (_, i) => formatMonto(i.total, i.moneda),
    },
    {
      title: 'UUID',
      dataIndex: 'uuid',
      key: 'uuid',
      width: 300,
      render: (v, i) => (
        <Space size={4}>
          <Text style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</Text>
          <Button
            size="small"
            type="text"
            icon={<FilePdfOutlined />}
            loading={generandoPdfUuid === i.uuid}
            onClick={() => verPdfCfdi(i.uuid)}
            title="Ver PDF"
          />
        </Space>
      ),
    },
  ];

  if (loadingEmpresas) return <Loader tip="Cargando..." />;

  return (
    <div>
      <Title level={3} style={{ marginTop: 0, marginBottom: 4 }}>Catálogo de CFDIs</Title>
      <Paragraph type="secondary" style={{ marginBottom: 20 }}>
        Todos los CFDIs descargados del SAT para la empresa seleccionada.
      </Paragraph>

      {rfc === EMPRESA_TODAS ? (
        <Card>
          <EmptyState description="Selecciona una empresa arriba para consultar su catálogo de CFDIs." />
        </Card>
      ) : (
        <>
          {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}

          <Card size="small" style={{ marginBottom: 16 }}>
            <Space wrap align="center">
              <Text strong>Descargar CFDIs del SAT:</Text>
              <DatePicker.RangePicker
                value={descargaRango}
                onChange={v => setDescargaRango(v ? [v[0], v[1]] : [null, null])}
                format="YYYY-MM-DD"
                placeholder={['Desde', 'Hasta']}
                disabledDate={current => !!current && current.isAfter(dayjs(), 'day')}
                disabled={descargaSolicitudIds != null || isDescargaSolicitudEnProceso}
              />
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                loading={solicitando || isDescargaSolicitudEnProceso}
                disabled={descargaSolicitudIds != null}
                onClick={solicitarDescarga}
              >
                Solicitar al SAT
              </Button>
              {!!descargaSolicitudIds?.length && (
                <Button loading={verificando || !!descargaPollIds} onClick={verificarDescarga}>
                  Verificar
                </Button>
              )}
            </Space>
          </Card>

          <Card
            size="small"
            title="Estado de descargas al SAT por mes"
            style={{ marginBottom: 16 }}
            extra={
              <Button
                type="text"
                size="small"
                icon={isHistorialColapsado ? <DownOutlined /> : <UpOutlined />}
                onClick={() => setIsHistorialColapsado(!isHistorialColapsado)}
              >
                {isHistorialColapsado ? 'Maximizar' : 'Minimizar'}
              </Button>
            }
          >
            {!isHistorialColapsado && (
              <SolicitudesEstadoPorMes
                solicitudes={solicitudesHistorial}
                conteoPorMes={conteoPorMes}
                loading={cargandoHistorial}
                onCancelar={handleCancelarMes}
              />
            )}
          </Card>

          <Tabs
            activeKey={subTab}
            onChange={setSubTab}
            items={SUB_TABS.map(t => ({ key: t.key, label: t.label }))}
            tabBarExtraContent={
              <Space>
                <Button icon={<SettingOutlined />} onClick={() => setColumnasModalOpen(true)}>
                  Editar Columnas del Excel
                </Button>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  loading={exportando}
                  onClick={descargarExcel}
                  style={{ background: '#198754', borderColor: '#198754' }}
                >
                  Exportar a Excel
                </Button>
              </Space>
            }
          />
          <ColumnasExcelModal visible={columnasModalOpen} onClose={() => setColumnasModalOpen(false)} />

          <Card size="small" style={{ marginBottom: 16 }}>
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={6}>
                <Select
                  value={direccion}
                  onChange={setDireccion}
                  style={{ width: '100%' }}
                  options={[
                    { value: 'TODOS', label: 'Emitidos y recibidos' },
                    { value: 'emitidos', label: 'Emitidos' },
                    { value: 'recibidos', label: 'Recibidos' },
                  ]}
                />
              </Col>
              <Col xs={24} sm={6}>
                <DatePicker.RangePicker
                  style={{ width: '100%' }}
                  value={rango ?? [null, null]}
                  onChange={v => setRango(v ? [v[0], v[1]] : null)}
                  format="YYYY-MM-DD"
                  placeholder={['Desde', 'Hasta']}
                />
              </Col>
              <Col xs={24} sm={6}>
                <Input
                  placeholder="Buscar por folio"
                  value={folio}
                  onChange={e => setFolio(e.target.value)}
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={6}>
                <Input
                  placeholder="Buscar por UUID"
                  value={uuid}
                  onChange={e => setUuid(e.target.value)}
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={6}>
                <Input
                  placeholder="Buscar por RFC (emisor o receptor)"
                  value={filtroRfc}
                  onChange={e => setFiltroRfc(e.target.value)}
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={6}>
                <InputNumber
                  placeholder="Monto Mínimo"
                  value={montoMin}
                  onChange={v => setMontoMin(v)}
                  style={{ width: '100%' }}
                  min={0}
                />
              </Col>
              <Col xs={24} sm={6}>
                <InputNumber
                  placeholder="Monto Máximo"
                  value={montoMax}
                  onChange={v => setMontoMax(v)}
                  style={{ width: '100%' }}
                  min={0}
                />
              </Col>
            </Row>
          </Card>

          <Card styles={{ body: { padding: 0 } }}>
            <Table<CfdiCatalogoItem>
              rowKey="uuid"
              columns={columns}
              dataSource={items}
              loading={loading}
              locale={{ emptyText: <Empty description="Ningún CFDI coincide con los filtros." style={{ padding: 24 }} /> }}
              pagination={{
                current: page,
                onChange: setPage,
                total,
                pageSize: PAGE_SIZE,
                showSizeChanger: false,
              }}
              size="middle"
              scroll={{ x: 1100 }}
            />
          </Card>
        </>
      )}
    </div>
  );
}
